import { LogLevel } from '@azure/msal-browser';
import { logger } from '../utils/logger';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
    authority: import.meta.env.VITE_MSAL_AUTHORITY,
    redirectUri:
      import.meta.env.VITE_MSAL_LOGIN_REDIRECTURI || window.location.origin,
    navigateToLoginRequestUrl: true, // Navigate back to the original URL after login (supports deep links)
  },
  cache: {
    cacheLocation: 'localStorage', // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set to false for better performance, true only for IE11
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ) => {
        if (containsPii || import.meta.env.VITE_MSAL_LOGGING === 'false') {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            logger.error(message);
            return;
          case LogLevel.Info:
            logger.info(message);
            return;
          case LogLevel.Verbose:
          case LogLevel.Trace:
            logger.debug(message);
            return;
          case LogLevel.Warning:
            logger.warn(message);
            return;
          default: {
            const error: never = level;
            throw new Error(`UNKNOWN_LOG_LEVEL_${error}`);
          }
        }
      },
    },
  },
};

// Validate required environment variables
if (!import.meta.env.VITE_MSAL_CLIENT_ID) {
  logger.error('❌ VITE_MSAL_CLIENT_ID is required for MSAL authentication');
} else {
  logger.debug(
    `✅ VITE_MSAL_CLIENT_ID is set to ${import.meta.env.VITE_MSAL_CLIENT_ID.slice(0, 6)}...`
  );
}

if (!import.meta.env.VITE_MSAL_AUTHORITY) {
  logger.error('❌ VITE_MSAL_AUTHORITY is required for MSAL authentication');
} else {
  logger.debug(
    `✅ VITE_MSAL_AUTHORITY is set to ${import.meta.env.VITE_MSAL_AUTHORITY.slice(0, 50)}...`
  );
}

if (!import.meta.env.VITE_MSAL_LOGIN_REDIRECTURI) {
  logger.error(
    '❌ VITE_MSAL_LOGIN_REDIRECTURI is required for MSAL authentication'
  );
} else {
  logger.debug(
    `✅ VITE_MSAL_LOGIN_REDIRECTURI is set to ${import.meta.env.VITE_MSAL_LOGIN_REDIRECTURI}`
  );
}

if (!import.meta.env.VITE_API_ENDPOINT) {
  logger.error('❌ VITE_API_ENDPOINT is required for GraphQL communication');
} else {
  logger.debug(
    `✅ VITE_API_ENDPOINT is set to ${import.meta.env.VITE_API_ENDPOINT}`
  );
}

if (!import.meta.env.VITE_MSAL_SCOPE_ENDPOINT) {
  logger.error(
    '❌ VITE_MSAL_SCOPE_ENDPOINT is required (e.g. api://<clientId>/access_as_user)'
  );
}

// Request the explicit API scope instead of `<clientId>/.default`.
// `.default` expands to statically configured API permissions and is a poor fit
// here because this SPA needs the backend's delegated user scope. The explicit
// scope keeps the requested resource, token audience, and MSAL cache lookup
// aligned with what the backend validates.
const protectedResources = {
  api: {
    endpoint: import.meta.env.VITE_API_ENDPOINT,
    scopes: {
      tfPulseApi: [import.meta.env.VITE_MSAL_SCOPE_ENDPOINT],
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [...protectedResources.api.scopes.tfPulseApi],
};
